import type { DnsechoRow, IpDetails } from '@shared/api'
import { classify } from './verdict'
import { toOperators } from './operators'

/**
 * A finished test, serialised for the visitor to keep.
 *
 * This is the local counterpart to Share: Share publishes the result to a link,
 * Export hands the visitor a file that never leaves their machine -- which for a
 * privacy tool is the more honest default for "keep a copy". The shape is stable
 * and self-describing so it is useful to a script, not just to us.
 */
export interface TestExport {
    tool: 'dnsleaktest.org'
    version: 1
    generatedAt: string
    connection: {
        ip?: string
        operator?: string
        asn?: number
        country?: string
        city?: string
    }
    verdict: {
        level: string
        organisations: number
        countries: number
    }
    operators: { name: string; queries: number; isYourIsp: boolean }[]
    resolvers: {
        ip: string
        queries: number
        operator?: string
        asn?: number
        country?: string
        city?: string
    }[]
}

export function buildExport(
    rows: DnsechoRow[],
    self: IpDetails | undefined,
    now: Date = new Date(),
): TestExport {
    const verdict = classify(rows, self)
    const operators = toOperators(rows, self)

    return {
        tool: 'dnsleaktest.org',
        version: 1,
        generatedAt: now.toISOString(),
        connection: {
            ip: (self as (IpDetails & { ip?: string }) | undefined)?.ip,
            operator: self?.asnOrg ?? self?.isp,
            asn: self?.asnNumber,
            country: self?.countryName ?? self?.cc,
            city: self?.cityName,
        },
        verdict: {
            level: verdict.level,
            organisations: verdict.organisations,
            countries: verdict.countries,
        },
        operators: operators.map((o) => ({ name: o.name, queries: o.count, isYourIsp: o.isSelf })),
        resolvers: rows.map((r) => ({
            ip: r.ip,
            queries: r.count,
            operator: r.asnOrg ?? r.isp,
            asn: r.asnNumber,
            country: r.countryName ?? r.cc,
            city: r.cityName,
        })),
    }
}

/** Triggers a browser download of the export as pretty-printed JSON. */
export function downloadExport(data: TestExport): void {
    const stamp = data.generatedAt.slice(0, 19).replace(/[:T]/g, '-')
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dns-leak-test-${stamp}.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    // revoke on the next tick so the click has definitely been handled
    setTimeout(() => URL.revokeObjectURL(url), 0)
}
